import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'

export default function NewListing() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [availableCodes, setAvailableCodes] = useState<{code: string}[]>([])
  const [formData, setFormData] = useState({
    code: '',
    address_street: '',
    address_city: '',
    address_state: 'AZ',
    address_zip: '',
    price: '',
    beds: '',
    baths: '',
    sqft: '',
    description: '',
    photos_url: '',
    listing_agent_name: '',
    listing_agent_phone: '',
    listing_agent_email: ''
  })

  useEffect(() => {
    fetchAvailableCodes()
  }, [])

  const fetchAvailableCodes = async () => {
    const { data } = await supabase
      .from('sign_codes')
      .select('code')
      .eq('status', 'available')
      .order('code')
    
    setAvailableCodes(data || [])
    if (data && data.length > 0) {
      setFormData(prev => ({ ...prev, code: data[0].code }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    // Parse photos from URL
    const photos = formData.photos_url
      ? formData.photos_url.split(',').map((url, i) => ({
          url: url.trim(),
          order: i
        }))
      : []

    const { error } = await supabase
      .from('listings')
      .insert({
        assigned_code: formData.code,
        address_street: formData.address_street,
        address_city: formData.address_city,
        address_state: formData.address_state,
        address_zip: formData.address_zip,
        price: parseInt(formData.price) || null,
        beds: parseFloat(formData.beds) || null,
        baths: parseFloat(formData.baths) || null,
        sqft: parseInt(formData.sqft) || null,
        description: formData.description,
        photos: photos,
        listing_agent_name: formData.listing_agent_name,
        listing_agent_phone: formData.listing_agent_phone,
        listing_agent_email: formData.listing_agent_email,
        status: 'active'
      })

    if (error) {
      alert('Error creating listing: ' + error.message)
    } else {
      // Update sign code status
      await supabase
        .from('sign_codes')
        .update({ status: 'active' })
        .eq('code', formData.code)
      
      navigate('/listings')
    }
    setLoading(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Create New Listing</h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Sign Code */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sign Code *
            </label>
            <select
              name="code"
              value={formData.code}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            >
              {availableCodes.map(code => (
                <option key={code.code} value={code.code}>{code.code}</option>
              ))}
            </select>
            {availableCodes.length === 0 && (
              <p className="text-sm text-red-600 mt-1">No available codes. Contact admin.</p>
            )}
          </div>

          {/* Address */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Street Address *
            </label>
            <input
              type="text"
              name="address_street"
              value={formData.address_street}
              onChange={handleChange}
              required
              placeholder="1234 E Main St"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
            <input
              type="text"
              name="address_city"
              value={formData.address_city}
              onChange={handleChange}
              placeholder="Phoenix"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
              <input
                type="text"
                name="address_state"
                value={formData.address_state}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Zip</label>
              <input
                type="text"
                name="address_zip"
                value={formData.address_zip}
                onChange={handleChange}
                placeholder="85018"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {/* Price & Specs */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder="599000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Beds</label>
              <input
                type="number"
                step="0.5"
                name="beds"
                value={formData.beds}
                onChange={handleChange}
                placeholder="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Baths</label>
              <input
                type="number"
                step="0.5"
                name="baths"
                value={formData.baths}
                onChange={handleChange}
                placeholder="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sqft</label>
              <input
                type="number"
                name="sqft"
                value={formData.sqft}
                onChange={handleChange}
                placeholder="2100"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {/* Photos */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photo URLs (comma-separated)
            </label>
            <input
              type="text"
              name="photos_url"
              value={formData.photos_url}
              onChange={handleChange}
              placeholder="https://dropbox.com/photo1.jpg, https://dropbox.com/photo2.jpg"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Agent Info */}
          <div className="md:col-span-2 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Listing Agent</h3>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Agent Name</label>
            <input
              type="text"
              name="listing_agent_name"
              value={formData.listing_agent_name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Agent Phone</label>
            <input
              type="tel"
              name="listing_agent_phone"
              value={formData.listing_agent_phone}
              onChange={handleChange}
              placeholder="(480) 555-0100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Agent Email</label>
            <input
              type="email"
              name="listing_agent_email"
              value={formData.listing_agent_email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
          </div>
        </div>

        <div className="mt-8 flex gap-4">
          <button
            type="submit"
            disabled={loading || availableCodes.length === 0}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Listing'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/listings')}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
